import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Button,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Article as ArticleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  ViewCarousel as CarouselIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Comment as CommentIcon,
  ThumbUp as ThumbUpIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AutoFixHigh as RewriteIcon,
  PictureAsPdf as PdfIcon,
  Share
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';

// Import SVG as ReactComponent
import { ReactComponent as PostReactions } from '../pages/post-reactions.svg';

// Create a separate PDFPreview component
const PDFPreview = React.memo(({ url, onOpenPdf }) => {
  const [pdfImage, setPdfImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAndRenderPDF = async () => {
      if (!url) return;
      
      setLoading(true);
      setError(null);

      try {
        // Fetch the PDF
        const response = await fetch(url);
        if (!isMounted) return;

        const blob = await response.blob();
        if (!isMounted) return;

        const arrayBuffer = await blob.arrayBuffer();
        if (!isMounted) return;

        // Load the PDF
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (!isMounted) return;
        
        // Get first page
        const page = await pdf.getPage(1);
        if (!isMounted) return;
        
        // Set scale for better quality
        const scale = 1.5;
        const viewport = page.getViewport({ scale });

        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Render PDF page to canvas
        await page.render({
          canvasContext: context,
          viewport
        }).promise;
        
        if (!isMounted) return;

        // Convert to image
        const image = canvas.toDataURL();
        setPdfImage(image);
      } catch (error) {
        if (isMounted) {
          console.error('Error rendering PDF:', error);
          setError('Failed to load PDF preview');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAndRenderPDF();

    return () => {
      isMounted = false;
      setPdfImage(null);
      setLoading(false);
      setError(null);
    };
  }, [url]);

  const containerStyle = useMemo(() => ({
    mb: 2,
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0',
      borderColor: '#d0d0d0'
    }
  }), []);

  if (!url) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No document available
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      onClick={() => onOpenPdf?.(url)}
      sx={containerStyle}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <PdfIcon sx={{ color: '#e94444' }} />
        <Typography variant="subtitle2">
          PDF Document
        </Typography>
        <OpenInNewIcon 
          sx={{ 
            ml: 'auto', 
            fontSize: '1rem', 
            color: '#666'
          }} 
        />
      </Box>

      <Box sx={{ 
        position: 'relative',
        minHeight: 200,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Loading preview...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <DocumentIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && pdfImage && (
          <Box sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            p: 2
          }}>
            <img 
              src={pdfImage} 
              alt="PDF preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: 4
              }}
            />
          </Box>
        )}

        {!loading && !error && !pdfImage && (
          <Box sx={{ 
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}>
            <DocumentIcon sx={{ fontSize: 40, color: '#666' }} />
            <Typography variant="body2" color="text.secondary">
              Click to open PDF
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

PDFPreview.displayName = 'PDFPreview';

const PostCard = ({ 
  post, 
  onCommentClick,
  onRemixClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Function to handle PDF click to open in new tab
  const handlePdfClick = useCallback((url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Helper function to format numbers (e.g., 1000 -> 1k)
  const formatNumber = (num) => {
    if (!num) return 0;
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num;
  };

  // Function to get the appropriate thumbnail URL
  const getThumbnailUrl = (post) => {
    // Skip thumbnails for documents
    if (post.format === 'Document' || post.media?.type === 'Document') {
      return null;
    }
    
    // Handle other media types
    if (post.media) {
      if (post.media.thumbnail) return post.media.thumbnail;
      if (post.media.url && !post.media.url.includes('.mp4') && !post.media.url.includes('/document/')) {
        return post.media.url;
      }
      if (post.media.items && post.media.items.length > 0) {
        return post.media.items[0].url || post.media.items[0].thumbnail;
      }
    }
    
    return null;
  };

  // Check if post has video
  const hasVideo = post.media && 
    (post.format === 'Video' || 
     post.media.type === 'video' || 
     (post.media.url && post.media.url.includes('/vid/')));

  // Get thumbnail URL for this post
  const thumbnailUrl = getThumbnailUrl(post);

  // Format the post URL
  const getPostUrl = () => {
    if (post.url) return post.url;
    if (post.document?.id) {
      return `https://www.linkedin.com/feed/update/urn:li:activity:${post.document.id}`;
    }
    if (post.id) {
      return `https://www.linkedin.com/feed/update/urn:li:activity:${post.id}`;
    }
    return null;
  };

  // Handle see more click
  const handleSeeMoreClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Inside the component, add this helper function
  const formatPostDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Check if timestamp is actually a date string in format "YYYY-MM-DD HH:MM:SS"
    if (typeof timestamp === 'string' && timestamp.includes('-') && timestamp.includes(':')) {
      try {
        // Parse the date string directly
        const [datePart, timePart] = timestamp.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute, second] = timePart.split(':').map(Number);
        
        const date = new Date(year, month - 1, day, hour, minute, second);
        
        if (!isNaN(date.getTime())) {
          // Format like "Apr 14, 2025 at 10:03 AM"
          const options = { month: 'short', day: 'numeric', year: 'numeric' };
          const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
          
          const dateStr = date.toLocaleDateString('en-US', options);
          const timeStr = date.toLocaleTimeString('en-US', timeOptions);
          
          return `${dateStr} at ${timeStr}`;
        }
      } catch (e) {
        console.error('Error parsing date string:', e, timestamp);
      }
    }
    
    // If not a date string or parsing failed, try as timestamp
    try {
      const date = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date timestamp:', timestamp);
        return timestamp.toString();
      }
      
      // Format like "Apr 14, 2025 at 10:03 AM"
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
      
      const dateStr = date.toLocaleDateString('en-US', options);
      const timeStr = date.toLocaleTimeString('en-US', timeOptions);
      
      return `${dateStr} at ${timeStr}`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return timestamp.toString();
    }
  };

  // Handle view click
  const handleViewClick = () => {
    const postUrl = getPostUrl();
    if (postUrl) {
      window.open(postUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Debug post data
  console.log('PostCard rendering with data:', {
    authorName: post.data?.author?.name || post.author?.name || 'LinkedIn User',
    profilePicture: post.data?.author?.profilePicture || post.author?.profilePicture,
    timestamp: post.postedAt?.timestamp,
    postDate: post.postedAt?.date
  });

  // Update the post preview section
  const renderPreview = useCallback(() => {
    // Handle documents
    if (post.format === 'Document' || post.media?.type === 'Document') {
      return (
        <PDFPreview 
          url={post.media?.url || post.carousel_document_link}
          onOpenPdf={handlePdfClick}
        />
      );
    }

    // Handle videos and images as before
    if (thumbnailUrl) {
      return (
        <Box 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          {hasVideo ? (
            <Box 
              sx={{ 
                width: '100%', 
                position: 'relative', 
                backgroundColor: '#000',
                '& video': {
                  width: '100%',
                  maxHeight: '600px',
                  objectFit: 'contain'
                }
              }}
            >
              <video
                controls
                playsInline
                preload="metadata"
                width="100%"
                poster={post.media.thumbnail}
              >
                <source src={post.media.url} type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
            </Box>
          ) : (
            <img 
              src={thumbnailUrl} 
              alt="Post content"
              style={{
                width: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
                display: 'block',
                maxWidth: '100%'
              }}
              onError={(e) => {
                console.error("Image load error for URL:", thumbnailUrl);
                if (!e.target.dataset.attempted && post.media && post.media.url) {
                  e.target.dataset.attempted = 'true';
                  console.log("Attempting with alternate URL:", post.media.url);
                  e.target.src = post.media.url;
                } else {
                  e.target.style.display = 'none';
                  e.target.parentElement.style.display = 'none';
                }
              }}
            />
          )}
        </Box>
      );
    }

    return null;
  }, [post, handlePdfClick, hasVideo, thumbnailUrl]);

  return (
    <Card 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateY(-2px)'
        },
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <CardContent sx={{ p: 2, pb: 1, flex: 1 }}>
        {/* Author and Date */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
          <Avatar 
            src={post.data?.author?.profilePicture || post.author?.profilePicture || ''}
            alt={post.data?.author?.name || post.author?.name || 'LinkedIn User'}
            sx={{ width: 40, height: 40, mr: 1.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.1, fontSize: '1rem', mb: 0.2 }}>
              {post.data?.author?.name || post.author?.name || 'LinkedIn User'}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{
                color: '#666', 
                fontSize: '0.75rem',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                mb: 0.2
              }}
            >
              {post.data?.author?.occupation || post.author?.headline || ''}
            </Typography>
            <Typography variant="caption" color="#666" sx={{ lineHeight: 1.2, display: 'block', fontSize: '0.7rem' }}>
              {post.postedAt?.date ? formatPostDate(post.postedAt.date) : 
               post.postedAt?.timestamp ? formatPostDate(post.postedAt.timestamp) : 
               post.document?.platform_created_at ? formatPostDate(post.document.platform_created_at) : 
               ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', pl: 2 }}>
            {(() => {
              // Log post format and media type for debugging
              console.log('Post format:', post.format, 'Media type:', post.media?.type, 'Media:', post.media);

              // Check for video first
              if (hasVideo || post.format === 'Video' || post.media?.type === 'video') {
                return (
                  <Tooltip title="Video">
                    <VideoIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px' }} />
                  </Tooltip>
                );
              }
              
              // Then check for images
              if (post.format === 'Image' || post.media?.type === 'image' || 
                  (post.media && !post.media.type && (post.media.url?.includes('.jpg') || post.media.url?.includes('.png')))) {
                return (
                  <Tooltip title="Image">
                    <ImageIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px' }} />
                  </Tooltip>
                );
              }
              
              // Check for carousel/multiple images
              if (post.format === 'Carousel' || (post.media?.items && post.media.items.length > 1)) {
                return (
                  <Tooltip title="Carousel">
                    <CarouselIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px' }} />
                  </Tooltip>
                );
              }
              
              // Check for documents
              if (post.format === 'Document' || post.document || post.media?.type === 'document' || 
                  (post.media && post.media.url?.includes('.pdf'))) {
                return (
                  <Tooltip title="Document">
                    <DocumentIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px' }} />
                  </Tooltip>
                );
              }
              
              // Default to text/article
              return (
                <Tooltip title="Text">
                  <ArticleIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px' }} />
                </Tooltip>
              );
            })()}
          </Box>
        </Box>
        
        {/* Post Content */}
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.5,
            color: '#000000',
            fontSize: '0.9rem',
            whiteSpace: 'pre-line',
            transition: 'all 0.3s ease-in-out',
            maxHeight: isExpanded ? '2000px' : '4.5em',
            width: '100%',
            maxWidth: '100%',
            wordBreak: 'break-word'
          }}
        >
          {post.content || post.document?.body || ''}
        </Typography>

        {/* See more button */}
        <Typography 
          variant="body2" 
          onClick={handleSeeMoreClick}
          sx={{ 
            color: '#0a66c2', 
            mb: 2,
            cursor: 'pointer',
            fontWeight: 500,
            display: 'inline-block',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          {isExpanded ? 'See less' : 'See more'}
        </Typography>
        
        {/* Post Preview */}
        {renderPreview()}
        
        {/* Reaction counts */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 'auto', 
          mb: 0.5, 
          justifyContent: 'space-between',
          flexWrap: 'wrap', // Allow wrapping for stats
          width: '100%'
        }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: '#f7f9fa',
              borderRadius: '12px',
              padding: '2px 8px',
              border: '1px solid #e0e0e0',
              marginBottom: '4px',
              marginRight: '4px'
            }}
          >
            <PostReactions width={36} height={16} />
            <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.8rem', color: '#666' }}>
              {formatNumber(post.stats?.totalReactions || post.stats?.likes || post.document?.likes_count || 0)}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: 'wrap', // Allow wrapping for comments/reposts stats
            marginBottom: '4px'
          }}>
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.8rem', 
              display: 'flex', 
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: '0.9rem', mr: 0.3 }} />
              {formatNumber(post.stats?.comments || post.document?.comments_count || 0)} comments
            </Typography>
            
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
              •
            </Typography>
            
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center'
            }}>
              <Share sx={{ fontSize: '0.9rem', mr: 0.3 }} />
              {formatNumber(post.stats?.reposts || 0)} reposts
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ 
        px: 2, 
        py: 1, 
        borderTop: '1px solid #f0f0f0', 
        justifyContent: 'space-between',
        flexWrap: 'nowrap', // Prevent wrapping
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexWrap: 'nowrap' // Prevent wrapping
        }}>
          {/* Comment button - responsive */}
          <Tooltip title="Comment">
            <Button
              size="small"
              sx={{ 
                textTransform: 'none', 
                color: 'var(--primary-color)',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(10, 102, 194, 0.08)'
                },
                minWidth: { xs: '32px', sm: '80px' },
                p: { xs: '4px', sm: '4px 8px' },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 0.5 }
                }
              }}
              onClick={() => onCommentClick?.(post)}
              startIcon={<CommentIcon fontSize="small" />}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Comment</Box>
            </Button>
          </Tooltip>

          {/* Remix button - responsive */}
          <Tooltip title="Remix">
            <Button
              size="small"
              sx={{ 
                textTransform: 'none', 
                color: 'var(--primary-color)',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(10, 102, 194, 0.08)'
                },
                minWidth: { xs: '32px', sm: '70px' },
                p: { xs: '4px', sm: '4px 8px' },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 0.5 }
                }
              }}
              onClick={() => onRemixClick?.(post)}
              startIcon={<RewriteIcon fontSize="small" />}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Remix</Box>
            </Button>
          </Tooltip>
        </Box>
        
        {/* View button - responsive */}
        <Tooltip title="View on LinkedIn">
          <Button 
            size="small"
            sx={{ 
              textTransform: 'none', 
              color: 'var(--primary-color)',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(10, 102, 194, 0.08)'
              },
              minWidth: { xs: '32px', sm: '60px' },
              p: { xs: '4px', sm: '4px 8px' },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 0.5 }
              }
            }}
            onClick={handleViewClick}
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>View</Box>
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default PostCard; 